import { Card, MessageBar, MessageBarBody, MessageBarTitle, Tab, TabList } from '@fluentui/react-components';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../common/PageHeader';
import { LoadingState } from '../../common/LoadingState';
import { ErrorState } from '../../common/ErrorState';
import { EmptyState } from '../../common/EmptyState';
import { StatusBadge } from '../../common/StatusBadge';
import { useSubscriptions } from '../../../hooks/useSubscriptions';
import { formatDateTime } from '../../../utils/date';
import { SyncButton } from './SyncButton';
import { SubscriptionsTab } from '../orders/SubscriptionsTab';
import { OrdersTab } from '../orders/OrdersTab';
import type { Client } from '../../../models/Client';
import type { Order } from '../../../models/Order';
import { clientService } from '../../../services/dataverse/clientService';
import { orderService } from '../../../services/dataverse/orderService';

export const ClientDetailPage = () => {
  const { id } = useParams();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('subscriptions');
  const [orders, setOrders] = useState<Order[]>([]);
  const { subscriptions } = useSubscriptions(id);

  const loadClient = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await clientService.getById(id);
      setClient(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load client.');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!id) return;
    try {
      const data = await orderService.getByClient(id);
      setOrders(data);
    } catch {
      // non-blocking
    }
  };

  useEffect(() => { void loadClient(); void loadOrders(); }, [id]);

  if (loading) return <LoadingState label="Loading client details..." />;
  if (error) return <ErrorState message={error} onRetry={() => void loadClient()} />;
  if (!client) return <EmptyState title="Client not found" description="The requested client does not exist in the current workspace." />;

  return (
    <div>
      <PageHeader title={client.cr_crm_companyname} subtitle={client.cr_crm_domain ?? 'No domain configured'} breadcrumbs={["Clients", client.cr_crm_companyname]} actions={[{ label: 'Back to clients', onClick: () => history.back() }]} />
      <Card>
        <p><strong>Status:</strong> <StatusBadge value={client.cr_crm_status} /></p>
        <p><strong>Email:</strong> {client.cr_crm_email ?? '—'}</p>
        <p><strong>Phone:</strong> {client.cr_crm_phone ?? '—'}</p>
        <p><strong>Address:</strong> {[client.cr_crm_address, client.cr_crm_postalcode, client.cr_crm_city, client.cr_crm_country].filter(Boolean).join(', ') || '—'}</p>
        <p><strong>Last sync:</strong> {formatDateTime(client.cr_crm_lastsyncat)}</p>
        <SyncButton onSync={() => void clientService.sync(client.cr_crm_clientid).then(() => loadClient())} />
      </Card>
      {client.cr_crm_syncerror ? (
        <MessageBar intent="warning" style={{ marginTop: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>Synchronization warning</MessageBarTitle>
            {client.cr_crm_syncerror}
          </MessageBarBody>
        </MessageBar>
      ) : null}
      <TabList
        selectedValue={activeTab}
        onTabSelect={(_, data) => setActiveTab(data.value as string)}
        style={{ marginTop: 16 }}
      >
        <Tab value="subscriptions">Subscriptions ({subscriptions.length})</Tab>
        <Tab value="orders">Orders ({orders.length})</Tab>
      </TabList>
      <div style={{ marginTop: 16 }}>
        {activeTab === 'subscriptions' && <SubscriptionsTab items={subscriptions} />}
        {activeTab === 'orders' && <OrdersTab items={orders} />}
      </div>
    </div>
  );
};
